import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Storage helper functions - Remember Me'e göre localStorage veya sessionStorage kullanır
const getStorage = (usePersistent = false) => {
  return usePersistent ? localStorage : sessionStorage;
};

const getToken = () => {
  // Önce localStorage'dan kontrol et (remember me için)
  const persistentToken = localStorage.getItem('accessToken');
  if (persistentToken) return persistentToken;
  
  // Yoksa sessionStorage'dan kontrol et
  return sessionStorage.getItem('accessToken');
};

const setToken = (token, usePersistent = false) => {
  const storage = getStorage(usePersistent);
  // Her iki storage'dan da temizle (tek bir yerde tutmak için)
  localStorage.removeItem('accessToken');
  sessionStorage.removeItem('accessToken');
  // Yeni token'ı uygun storage'a kaydet
  storage.setItem('accessToken', token);
};

const removeToken = () => {
  localStorage.removeItem('accessToken');
  sessionStorage.removeItem('accessToken');
};

// Tüm kullanıcı verilerini temizle (güvenli çıkış için)
const clearAllUserData = () => {
  // Token'ları temizle
  removeToken();
  // Login attempts'ı temizle (çıkış yaptıktan sonra sıfırlanmalı)
  localStorage.removeItem('loginAttempts');
  // Diğer potansiyel kullanıcı verilerini de temizle
  // (gelecekte eklenebilecek diğer storage verileri için)
};

// Session timeout: 30 dakika (milisaniye cinsinden)
const SESSION_TIMEOUT = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);

  // Session timeout ve inactivity yönetimi
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        // 30 dakika inaktiviteden sonra oturumu kapat
        removeToken();
        setUser(null);
      }, SESSION_TIMEOUT);
    }
  }, [user]);

  // Kullanıcı aktivitesini dinle
  useEffect(() => {
    if (user) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
      });

      resetInactivityTimer();

      return () => {
        events.forEach(event => {
          window.removeEventListener(event, resetInactivityTimer);
        });
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
      };
    }
  }, [user, resetInactivityTimer]);

  const checkAuth = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(false);

    try {
      const userInfo = await authAPI.me();
      setUser(userInfo);
    } catch (error) {
      if (error.status === 401 || error.message === 'UNAUTHORIZED') {
        console.error('Oturum geçersiz:', error);
      }
      removeToken();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await authAPI.login(email, password);
      if (response.accessToken) {
        setToken(response.accessToken, rememberMe);

        // Login sonrası user bilgisini tazece çek
        const userInfo = await authAPI.me();
        setUser(userInfo);
        return { success: true };
      }
      return { success: false, error: 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.' };
    } catch (error) {
      // Generic error message - bilgi sızıntısını önlemek için
      return { success: false, error: 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.' };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      return { success: true };
    } catch (error) {
      // Backend hatası olsa bile local veriyi temizle
      console.warn('Logout API hatası:', error);
      return { success: true, warning: 'Sunucuya bağlanılamadı ancak yerel oturum kapatıldı.' };
    } finally {
      // Güvenli çıkış: Tüm kullanıcı verilerini temizle
      clearAllUserData();
      setUser(null);
      
      // Inactivity timer'ı temizle
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      // Event listener'ları temizlemek için window event'lerini kaldır
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    }
  };

  const refreshMe = useCallback(async () => {
    const token = getToken();
    if (!token) return null;
    const userInfo = await authAPI.me();
    setUser(userInfo);
    return userInfo;
  }, []);

  return (
      <AuthContext.Provider value={{ user, login, logout, loading, refreshMe }}>
        {!loading && children}
      </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);